"use client";

import { Instagram, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i} className="h-5 w-32 bg-muted/40 animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="group/nav">
          <Link to={item.href || "#"} className="text-foreground inline-flex items-center text-[0.9375rem] font-default transition-all duration-240 hover:text-primary">
            <span aria-hidden="true" className="mr-0 inline-block h-1.5 w-1.5 rounded-lg bg-primary opacity-0 -translate-x-2 transition-all duration-240 group-hover/nav:opacity-100 group-hover/nav:translate-x-0 group-hover/nav:mr-3" />
            <span className="transition-all duration-240">{item.label}</span>
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1630" id="global-standard-footer" className="bg-background text-foreground relative font-default">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px" style={{
      background: "linear-gradient(to right, hsla(262, 48%, 30%, 0) 0%, hsla(262, 48%, 30%, 0.7) 50%, hsla(262, 48%, 30%, 0) 100%)"
    }} />

      <div ref={ref} className={`mx-auto max-w-[1440px] px-6 md:px-16 py-24 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {}
          <div className="md:col-span-5 flex flex-col gap-6">
            <Link to="/" aria-label="Global Standard Technology — Home" className="inline-block">
              <WvcLogo className="h-9 w-auto" />
            </Link>

            <p className="text-[1rem] leading-[1.65] font-default text-muted-foreground max-w-md">
              Precision industrial converting services for North American manufacturers. Delivering excellence in die-cutting, slitting, and lamination.
            </p>

            <p className="font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }}>· Mon–Fri · 8:00 AM – 6:00 PM&nbsp; pt</p>

            {}
            <div aria-hidden="true" className="h-px w-60 mt-2" style={{
            background: "linear-gradient(to right, hsla(262, 48%, 30%, 0.8) 0%, hsla(262, 48%, 30%, 0) 100%)"
          }} />
          </div>

          {}
          <div className="md:col-span-3 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 01 / NAVIGATION
            </h3>
            <MenuProvider menu_id="4">
              <FooterNavConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 02 / CONNECT
            </h3>
            <div className="flex items-center gap-5">
              <Link aria-label="Global Standard Technology on Instagram" className="underline cursor-pointer" href="whatsapp://send?phone=526644166474" newTab>
                <Instagram strokeWidth={1.25} />
              </Link>
              <Link aria-label="Global Standard Technology on LinkedIn" className="h-6 w-6 underline cursor-pointer" href="https://www.linkedin.com/company/gst-na/" newTab>
                <Linkedin strokeWidth={1.25} />
              </Link>
            </div>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 03 / INQUIRIES
            </h3>
            <Link to="tel:+18005550199" className="font-default italic text-foreground transition-colors duration-240 !text-xl !w-[230px] hover:text-primary" style={{
            fontSize: "clamp(1.25rem, 1.5vw, 1.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.015em",
            lineHeight: 1.15
          }}><span className="!text-xl">USA (858) 524-9057<br />MEXICO (664) 416-6474</span></Link>
            <Link style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }} className="!text-base underline cursor-pointer" href="mailto:rkim@gstna.com" newTab><span>rkim@gstna.com</span></Link>

            <Button asChild className="mt-2 self-start bg-primary text-primary-foreground border border-primary hover:bg-primary/90 font-mono uppercase rounded-lg group/btn" style={{
            fontSize: "0.75rem",
            letterSpacing: "0.2em",
            padding: "18px 28px",
            height: "auto"
          }}>
              <Link to="/ContactPage">
                <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-lg bg-primary-foreground opacity-0 -translate-x-3 mr-0 transition-all duration-240 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:mr-2" />
                Request a Quote
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="mt-20 h-px w-full" style={{
        background: "linear-gradient(to right, hsla(262, 48%, 30%, 0) 0%, hsla(262, 48%, 30%, 0.5) 50%, hsla(262, 48%, 30%, 0) 100%)"
      }} />

        {}
        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>© 2026 GLOBAL STANDARD TECHNOLOGY · ALL RIGHTS RESERVED</p>
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase inline-flex items-center gap-2" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>
            <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
            ENGINEERED FOR PRECISION
          </p>
        </div>
      </div>
    </section>;
}